import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, MapPin, LogOut, Trash2, Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useShop, money } from "../lib/shop";
import { toast } from "sonner";

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0033A0]";

const AuthForms = () => {
    const { refreshUser } = useShop();
    const [mode, setMode] = useState("login");
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setNotice("");
        setBusy(true);
        try {
            if (mode === "forgot") {
                const { data } = await api.post("/auth/forgot-password", { email: form.email });
                setNotice(data.message);
            } else if (mode === "register") {
                await api.post("/auth/register", form);
                await refreshUser();
            } else {
                await api.post("/auth/login", { email: form.email, password: form.password });
                await refreshUser();
            }
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail, "Something went wrong"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md mx-auto" data-testid="account-auth">
            <h1 className="font-heading text-2xl font-extrabold text-slate-900 text-center mb-6">
                {mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : "Reset Password"}
            </h1>
            {error && <p className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3" role="alert" data-testid="account-error">{error}</p>}
            {notice && <p className="mb-4 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-3" data-testid="account-notice">{notice}</p>}
            <form onSubmit={submit} className="space-y-4">
                {mode === "register" && (
                    <div>
                        <label htmlFor="acc-name" className="text-sm font-bold text-slate-900">Full name</label>
                        <input id="acc-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="register-name" />
                    </div>
                )}
                <div>
                    <label htmlFor="acc-email" className="text-sm font-bold text-slate-900">Email</label>
                    <input id="acc-email" type="email" required autoCapitalize="none" autoCorrect="off" spellCheck={false} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="account-email" />
                </div>
                {mode !== "forgot" && (
                    <div>
                        <label htmlFor="acc-password" className="text-sm font-bold text-slate-900">Password {mode === "register" && "(min 8 characters)"}</label>
                        <div className="relative">
                            <input id="acc-password" type={showPassword ? "text" : "password"} required minLength={mode === "register" ? 8 : 1}
                                autoCapitalize="none" autoCorrect="off" spellCheck={false}
                                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                                className={`${inputCls} pr-11`} data-testid="account-password" />
                            <button type="button" onClick={() => setShowPassword(!showPassword)}
                                aria-label={showPassword ? "Hide password" : "Show password"} data-testid="account-password-toggle"
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#0033A0]">
                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>
                )}
                <button type="submit" disabled={busy} data-testid="account-submit"
                    className="w-full rounded-full bg-[#0033A0] py-3 font-heading font-bold text-white hover:bg-[#002277] disabled:opacity-50">
                    {busy ? "Please wait…" : mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : "Send Reset Link"}
                </button>
            </form>
            <div className="mt-4 text-sm text-center space-y-2">
                {mode !== "login" && (
                    <p><button type="button" onClick={() => setMode("login")} className="text-[#0033A0] font-bold hover:underline" data-testid="switch-login">Back to sign in</button></p>
                )}
                {mode === "login" && (
                    <>
                        <p><button type="button" onClick={() => setMode("register")} className="text-[#0033A0] font-bold hover:underline" data-testid="switch-register">Create an account</button></p>
                        <p><button type="button" onClick={() => setMode("forgot")} className="text-slate-500 hover:underline" data-testid="switch-forgot">Forgot your password?</button></p>
                    </>
                )}
            </div>
        </div>
    );
};

const OrdersTab = () => {
    const [orders, setOrders] = useState([]);
    useEffect(() => {
        api.get("/account/orders").then((r) => setOrders(r.data)).catch(() => {});
    }, []);
    if (!orders.length)
        return <p className="text-slate-500 text-sm" data-testid="orders-empty">No orders yet — when you order, they'll appear here.</p>;
    return (
        <div className="space-y-4" data-testid="account-orders">
            {orders.map((o) => (
                <div key={o.order_number} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`account-order-${o.order_number}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                            <p className="font-heading font-extrabold text-[#0033A0]">{o.order_number}</p>
                            <p className="text-xs text-slate-400">{new Date(o.created_at).toLocaleString("en-GB")}</p>
                        </div>
                        <div className="text-right">
                            <p className="font-heading font-extrabold">{money(o.total)}</p>
                            <p className="text-xs text-slate-500">{o.status} · {o.payment_status}</p>
                        </div>
                    </div>
                    <ul className="mt-3 text-sm text-slate-600 space-y-1">
                        {o.items.map((i, idx) => (
                            <li key={idx}>{i.qty} × {i.name}{i.variation ? ` (${i.variation})` : ""}</li>
                        ))}
                    </ul>
                    <Link to={`/track/${o.order_number}?token=${o.tracking_token}`} data-testid={`track-${o.order_number}`}
                        className="mt-3 inline-block text-sm font-bold text-[#0033A0] hover:underline">
                        Track order
                    </Link>
                </div>
            ))}
        </div>
    );
};

const AddressesTab = ({ user, refreshUser }) => {
    const [form, setForm] = useState({ label: "Home", line1: "", line2: "", city: "Reading", postcode: "" });
    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/account/addresses", form);
            setForm({ label: "Home", line1: "", line2: "", city: "Reading", postcode: "" });
            await refreshUser();
            toast.success("Address saved");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };
    const remove = async (id) => {
        await api.delete(`/account/addresses/${id}`).catch(() => {});
        await refreshUser();
    };
    return (
        <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3" data-testid="address-list">
                {(user.addresses || []).length === 0 && <p className="text-slate-500 text-sm">No saved addresses yet.</p>}
                {(user.addresses || []).map((a) => (
                    <div key={a.id} className="bg-white rounded-xl border border-slate-200 p-4 flex justify-between items-start" data-testid={`address-${a.id}`}>
                        <div className="text-sm">
                            <p className="font-bold">{a.label}</p>
                            <p className="text-slate-600">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.postcode}</p>
                        </div>
                        <button type="button" onClick={() => remove(a.id)} aria-label="Delete address" data-testid={`delete-address-${a.id}`} className="text-red-500 p-1.5 hover:bg-red-50 rounded-full">
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                ))}
            </div>
            <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 p-5 space-y-3 h-fit" data-testid="address-form">
                <p className="font-heading font-bold">Add an address</p>
                <input required placeholder="Address line 1" value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} className={inputCls} data-testid="address-line1" />
                <input placeholder="Address line 2" value={form.line2} onChange={(e) => setForm({ ...form, line2: e.target.value })} className={inputCls} data-testid="address-line2" />
                <div className="grid grid-cols-2 gap-3">
                    <input required placeholder="Town / City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} data-testid="address-city" />
                    <input required placeholder="Postcode" value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} className={inputCls} data-testid="address-postcode" />
                </div>
                <button type="submit" data-testid="address-save" className="rounded-full bg-[#0033A0] px-6 py-2.5 font-heading font-bold text-white text-sm hover:bg-[#002277]">
                    Save Address
                </button>
            </form>
        </div>
    );
};

export default function Account() {
    const { user, logout, refreshUser } = useShop();
    const [tab, setTab] = useState("orders");
    const [verifyMsg, setVerifyMsg] = useState("");

    if (user === undefined) return <main className="min-h-[50vh] bg-slate-50" data-testid="account-loading" />;

    const resendVerification = async () => {
        try {
            const { data } = await api.post("/auth/resend-verification");
            setVerifyMsg(data.message);
        } catch (err) {
            setVerifyMsg(formatApiError(err.response?.data?.detail, "Could not send right now"));
        }
    };

    return (
        <main data-testid="account-page" className="bg-slate-50 min-h-screen py-10 md:py-16">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {!user ? (
                    <AuthForms />
                ) : user.role === "owner" || user.role === "staff" ? (
                    <div className="bg-white rounded-2xl border border-slate-200 p-10 max-w-md mx-auto text-center" data-testid="account-staff-notice">
                        <h1 className="font-heading text-2xl font-extrabold text-slate-900 mb-2">You're signed in as staff</h1>
                        <p className="text-slate-500 text-sm mb-6">
                            {user.email} is a {user.role} account. Customer sign-in is for shoppers — use the Store Manager for admin work.
                        </p>
                        <div className="flex flex-col gap-3">
                            <Link to="/admin" data-testid="account-goto-admin"
                                className="rounded-full bg-[#0033A0] px-8 py-3 font-heading font-bold text-white hover:bg-[#002277]">
                                Go to Store Manager
                            </Link>
                            <button type="button" onClick={logout} data-testid="account-staff-logout"
                                className="text-[#0033A0] font-bold hover:underline text-sm">
                                Sign out
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                            <div>
                                <h1 className="font-heading text-3xl font-extrabold text-slate-900" data-testid="account-greeting">
                                    Hello, {user.name}
                                </h1>
                                <p className="text-sm text-slate-500">{user.email}</p>
                            </div>
                            <button type="button" onClick={logout} data-testid="account-logout"
                                className="inline-flex items-center gap-2 rounded-full border-2 border-[#0033A0] px-5 py-2 text-sm font-bold text-[#0033A0] hover:bg-slate-100">
                                <LogOut className="h-4 w-4" /> Sign out
                            </button>
                        </div>
                        {!user.verified && (
                            <div className="mb-6 rounded-2xl bg-amber-50 border border-amber-200 px-5 py-4 flex flex-wrap items-center justify-between gap-3" data-testid="verify-banner">
                                <p className="text-sm text-amber-900">
                                    <strong>Verify your email</strong> — we sent a link to {user.email}. {verifyMsg}
                                </p>
                                <button type="button" onClick={resendVerification} data-testid="resend-verification"
                                    className="rounded-full bg-[#0033A0] px-5 py-2 text-sm font-bold text-white hover:bg-[#002277]">
                                    Resend email
                                </button>
                            </div>
                        )}
                        <div className="flex gap-2 mb-6" role="tablist">
                            <button type="button" role="tab" aria-selected={tab === "orders"} onClick={() => setTab("orders")} data-testid="tab-orders"
                                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-heading font-bold ${tab === "orders" ? "bg-[#0033A0] text-white" : "bg-white border-2 border-slate-200 text-slate-600"}`}>
                                <Package className="h-4 w-4" /> Orders
                            </button>
                            <button type="button" role="tab" aria-selected={tab === "addresses"} onClick={() => setTab("addresses")} data-testid="tab-addresses"
                                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-heading font-bold ${tab === "addresses" ? "bg-[#0033A0] text-white" : "bg-white border-2 border-slate-200 text-slate-600"}`}>
                                <MapPin className="h-4 w-4" /> Addresses
                            </button>
                        </div>
                        {tab === "orders" ? <OrdersTab /> : <AddressesTab user={user} refreshUser={refreshUser} />}
                    </>
                )}
            </div>
        </main>
    );
}
